import {
  buildUrl,
  defaultFetchHeaders,
  fetchJson,
  normalizeIdentityEmail,
} from "../../../utils";
import { ContactRole, LocationContact } from "../../types";
import { errorMessageFromResponse } from "../../../apiError";
import { suppressRealtimeEvent } from "../../realtime/suppressRealtime";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const locationBase = `${backend}/location`;

const normalizeContact = (contact: any): LocationContact => ({
  id: contact?.id,
  userId: contact?.uid ?? contact?.userId,
  active: contact?.active ?? undefined,
  inviteStatus: contact?.inviteStatus ?? contact?.invite_status,
  firstName: contact?.firstName ?? contact?.first_name ?? "",
  lastName: contact?.lastName ?? contact?.last_name ?? "",
  email: contact?.email ?? "",
  phone: contact?.phone ?? "",
  role: contact?.role,
});

const jsonHeaders = async () => ({
  ...(await defaultFetchHeaders()),
  "Content-Type": "application/json",
});

export async function listLocationContacts(
  locationId: string
): Promise<LocationContact[]> {
  const res = await fetchJson<LocationContact[]>(
    buildUrl(`${locationBase}/contact/all`, { id: locationId }),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch contacts"
  );
  return res.map(normalizeContact);
}

export async function addLocationContact(payload: {
  locationId: string;
  contact: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string | null;
    role: ContactRole;
  };
}): Promise<LocationContact> {
  suppressRealtimeEvent("location.changed", payload.locationId);
  const res = await fetchJson<LocationContact>(
    buildUrl(`${locationBase}/contact/create`, { id: payload.locationId }),
    {
      method: "POST",
      credentials: "include",
      headers : await jsonHeaders(),
      body: JSON.stringify({
        firstName: payload.contact.firstName || undefined,
        lastName: payload.contact.lastName || undefined,
        email: payload.contact.email
          ? normalizeIdentityEmail(payload.contact.email)
          : undefined,
        phone: payload.contact.phone ?? undefined,
        role: payload.contact.role || "staff",
      }),
    },
    "Failed to add contact"
  );
  return normalizeContact(res);
}

export async function updateLocationContact(payload: {
  contactId: string;
  contact: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string | null;
    role?: ContactRole;
  };
}): Promise<LocationContact> {
  suppressRealtimeEvent("location.changed");
  const res = await fetchJson<LocationContact>(
    buildUrl(`${locationBase}/contact/edit`, {
      contact_id: payload.contactId,
    }),
    {
      method: "PUT",
      credentials: "include",
      headers : await jsonHeaders(),
      body: JSON.stringify({
        firstName: payload.contact.firstName || undefined,
        lastName: payload.contact.lastName || undefined,
        email: payload.contact.email
          ? normalizeIdentityEmail(payload.contact.email)
          : undefined,
        phone: payload.contact.phone ?? undefined,
        role: payload.contact.role || undefined,
      }),
    },
    "Failed to update contact"
  );
  return normalizeContact(res);
}

export async function revokeLocationContact(
  locationId: string,
  contactId: string
): Promise<void> {
  suppressRealtimeEvent("location.changed", locationId);
  const res = await fetch(
    buildUrl(`${locationBase}/contact/revoke/access`, {
      id: locationId,
      contact_id: contactId,
    }),
    {
      method: "PUT",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    }
  );
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to revoke contact")
    );
  }
}
